import { SpanStatusCode } from "@opentelemetry/api";

import { RootSpan } from "../types";

export type ErrorSource = "console" | "source" | "report" | "custom";

export interface CapturedError {
  source: ErrorSource;
  type?: string;
  message: string;
  stack?: string;
  causes?: any[];
  timestamp: number;
  [key: string]: any;
}

export class ErrorCollector {
  private currentRootSpan: RootSpan = undefined;
  private _listenersSet = false;

  public subscribe(rootSpan: RootSpan) {
    this.currentRootSpan = rootSpan;
    if (!this._listenersSet) {
      this._setupGlobalListeners();
      this._listenersSet = true;
    }
  }

  public unsubscribe() {
    this.currentRootSpan = undefined;
  }

  public addError(
    error: unknown,
    context: Record<string, any> = {},
    source: ErrorSource = "custom",
  ) {
    const errObj = normalizeError(error, source);
    const event: CapturedError = {
      ...errObj,
      ...context,
      source: errObj.source ?? source,
      message: errObj.message ?? "Unknown error",
      timestamp: Date.now(),
    };
    if (this.currentRootSpan && typeof this.currentRootSpan.addEvent === "function") {
      this.currentRootSpan.addEvent("exception", event);
      if (typeof this.currentRootSpan.setStatus === "function") {
        this.currentRootSpan.setStatus({ code: SpanStatusCode.ERROR, message: event.message });
      }
    }
  }

  private _setupGlobalListeners() {
    // 1. Capture console.error
    const origConsoleError = window.console.error;
    window.console.error = (...args: any[]) => {
      this.addError(args[0], { args }, "console");
      origConsoleError.apply(window.console, args);
    };

    // 2. Capture window.onerror (unhandled exceptions)
    window.addEventListener("error", (event: ErrorEvent) => {
      this.addError(event.error || event.message, { event }, "source");
    });

    // 3. Capture unhandled promise rejections
    window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
      this.addError(event.reason, { event }, "source");
    });

    // 4. Capture ReportingObserver (if available)
    if (typeof window.ReportingObserver === "function") {
      try {
        const observer = new window.ReportingObserver(
          (reports: any[]) => {
            for (const report of reports) {
              this.addError(report, { report }, "report");
            }
          },
          { types: ["crash", "deprecation", "intervention"], buffered: true },
        );
        observer.observe();
      } catch {}
    }
  }
}

function normalizeError(error: unknown, source: ErrorSource): Partial<CapturedError> {
  if (error instanceof Error) {
    return {
      source,
      type: error.name,
      message: error.message,
      stack: error.stack,
      causes: (error as any).causes || undefined,
    };
  } else if (typeof error === "string") {
    return { source, message: error };
  } else if (typeof error === "object" && error !== null) {
    return { source, ...error } as any;
  } else {
    return { source, message: String(error) };
  }
}
